import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    Renderer2,
} from "@angular/core";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

import { DataService } from "../../../../providers/data/data.service";
import { GET_PRODUCT_RETAIL_PRICE_INFO } from "../../product-list.graphql";

type RetailPriceMarket = {
    marketplace: string;
    countryName: string;
    currency: string;
    retailAvg30?: number | null;
    status: string;
};

@Component({
    selector: "vsf-retail-price-hint",
    templateUrl: "./retail-price-hint.component.html",
    styleUrls: ["./retail-price-hint.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RetailPriceHintComponent implements OnDestroy {
    @Input() productId: string;

    open = false;
    loading = false;
    prices: RetailPriceMarket[] = [];

    private currentTarget: HTMLElement | null = null;
    private loadSub: Subscription | null = null;
    private loadedProductId: string | null = null;
    private popoverNode: HTMLElement | null = null;

    private readonly marketFlagSrcByCode: Record<string, string> = {
        DE: "assets/icons/flag-de.svg",
        FR: "assets/icons/flag-fr.svg",
        ES: "assets/icons/flag-es.svg",
        IT: "assets/icons/flag-it.svg",
        UK: "assets/icons/flag-uk.svg",
        GB: "assets/icons/flag-uk.svg",
    };

    constructor(
        private dataService: DataService,
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2,
        private hostRef: ElementRef<HTMLElement>,
    ) {}

    ngOnDestroy(): void {
        this.loadSub?.unsubscribe();
        this.removePopover();
    }

    @HostListener("window:scroll")
    @HostListener("window:resize")
    onViewportChange(): void {
        if (this.open) {
            this.updatePosition();
        }
    }

    onTriggerEnter(event: Event): void {
        const target = event.currentTarget as HTMLElement | null;
        if (!target || !this.productId) {
            return;
        }
        this.currentTarget = target;
        this.open = true;
        this.loadPrices();
        this.showPopover();
    }

    onTriggerLeave(): void {
        this.open = false;
        this.removePopover();
        this.cdr.markForCheck();
    }

    onTriggerClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    private marketFlagSrc(marketplace: string): string | null {
        const code = (marketplace || "").trim().toUpperCase();
        return this.marketFlagSrcByCode[code] ?? null;
    }

    private formatRetailPrice(
        price: number | null | undefined,
        currency: string
    ): string {
        if (price == null) {
            return "";
        }
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price / 100);
    }

    private showPopover(): void {
        this.removePopover();
        this.popoverNode = this.renderer.createElement("div");
        Object.assign(this.popoverNode!.style, {
            position: "fixed",
            zIndex: "9999",
            minWidth: "13.5rem",
            maxHeight: "80vh",
            overflowY: "auto",
            padding: "0.75rem 0.9rem",
            border: "1px solid #dfe3ea",
            borderRadius: "0.65rem",
            background: "#fff",
            color: "#344054",
            boxShadow:
                "0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)",
            whiteSpace: "normal",
            fontFamily: "Quicksand, sans-serif",
            pointerEvents: "none",
        });
        this.renderPopoverContent();
        this.renderer.appendChild(document.body, this.popoverNode);
        this.updatePosition();
    }

    private removePopover(): void {
        if (this.popoverNode && this.popoverNode.parentNode) {
            this.renderer.removeChild(document.body, this.popoverNode);
        }
        this.popoverNode = null;
    }

    private renderPopoverContent(): void {
        if (!this.popoverNode) {
            return;
        }
        this.popoverNode.innerHTML = "";

        // Title
        const title = this.renderer.createElement("p");
        Object.assign(title.style, {
            margin: "0 0 0.55rem",
            color: "#202939",
            fontSize: "0.8rem",
            fontWeight: "600",
        });
        title.textContent = "Retail prices";
        this.renderer.appendChild(this.popoverNode, title);

        if (this.loading) {
            const msg = this.renderer.createElement("p");
            Object.assign(msg.style, {
                margin: "0",
                color: "#707d96",
                fontSize: "0.8rem",
            });
            msg.textContent = "Loading…";
            this.renderer.appendChild(this.popoverNode, msg);
            return;
        }

        if (this.prices.length === 0) {
            const msg = this.renderer.createElement("p");
            Object.assign(msg.style, {
                margin: "0",
                color: "#707d96",
                fontSize: "0.8rem",
            });
            msg.textContent = "No retail prices";
            this.renderer.appendChild(this.popoverNode, msg);
            return;
        }

        for (const price of this.prices) {
            const row = this.renderer.createElement("div");
            Object.assign(row.style, {
                display: "flex",
                minHeight: "1.6rem",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.75rem",
                fontSize: "0.8125rem",
            });

            const country = this.renderer.createElement("span");
            Object.assign(country.style, {
                display: "inline-flex",
                alignItems: "center",
                minWidth: "0",
            });

            const flagSrc = this.marketFlagSrc(price.marketplace);
            if (flagSrc) {
                const flag = this.renderer.createElement("img");
                flag.src = flagSrc;
                flag.alt = price.countryName;
                Object.assign(flag.style, {
                    width: "1.25rem",
                    height: "0.85rem",
                    flex: "0 0 auto",
                    marginRight: "0.4rem",
                    objectFit: "cover",
                    border: "1px solid #dfe3ea",
                    borderRadius: "2px",
                });
                this.renderer.appendChild(country, flag);
            }

            const countryText = this.renderer.createText(price.countryName);
            this.renderer.appendChild(country, countryText);
            this.renderer.appendChild(row, country);

            const amount = this.renderer.createElement("span");
            Object.assign(amount.style, {
                flex: "0 0 auto",
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
            });
            amount.textContent = this.formatRetailPrice(
                price.retailAvg30,
                price.currency
            );
            this.renderer.appendChild(row, amount);
            this.renderer.appendChild(this.popoverNode, row);
        }
    }

    private updatePosition(): void {
        if (!this.currentTarget || !this.popoverNode) {
            return;
        }

        const triggerRect = this.currentTarget.getBoundingClientRect();

        // Hide popover if trigger is off-screen
        if (
            triggerRect.bottom < 0 ||
            triggerRect.top > window.innerHeight ||
            triggerRect.right < 0 ||
            triggerRect.left > window.innerWidth
        ) {
            this.removePopover();
            this.open = false;
            this.cdr.markForCheck();
            return;
        }

        const popoverWidth = this.popoverNode.offsetWidth || 220;
        const popoverHeight = this.popoverNode.offsetHeight || 160;
        const gap = 6;
        const margin = 12;

        // Horizontal: center on icon, clamp to viewport
        let left =
            triggerRect.left + triggerRect.width / 2 - popoverWidth / 2;
        if (left + popoverWidth > window.innerWidth - margin) {
            left = window.innerWidth - popoverWidth - margin;
        }
        if (left < margin) {
            left = margin;
        }

        // Vertical: prefer below icon, flip above if not enough space
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        let top: number;
        if (spaceBelow < popoverHeight + gap && spaceAbove > spaceBelow) {
            top = triggerRect.top - popoverHeight - gap;
        } else {
            top = triggerRect.bottom + gap;
        }

        // Clamp inside viewport
        top = Math.max(
            margin,
            Math.min(top, window.innerHeight - popoverHeight - margin)
        );

        this.popoverNode.style.top = `${top}px`;
        this.popoverNode.style.left = `${left}px`;
    }

    private loadPrices(): void {
        if (this.loadedProductId === this.productId && this.prices.length > 0) {
            this.renderPopoverContent();
            setTimeout(() => this.updatePosition(), 0);
            return;
        }
        this.loadSub?.unsubscribe();
        this.loading = true;
        this.prices = [];
        this.renderPopoverContent();
        this.loadSub = this.dataService
            .query<
                {
                    productRetailPriceInfo: {
                        prices: RetailPriceMarket[];
                    } | null;
                },
                { productId: string }
            >(GET_PRODUCT_RETAIL_PRICE_INFO, { productId: this.productId })
            .pipe(take(1))
            .subscribe({
                next: ({ productRetailPriceInfo }) => {
                    this.prices = (productRetailPriceInfo?.prices ?? []).filter(
                        (price) =>
                            price.status === "FOUND" &&
                            price.retailAvg30 != null &&
                            price.retailAvg30 > 0
                    );
                    this.loadedProductId = this.productId;
                    this.loading = false;
                    this.renderPopoverContent();
                    setTimeout(() => this.updatePosition(), 0);
                },
                error: () => {
                    this.prices = [];
                    this.loading = false;
                    this.renderPopoverContent();
                    setTimeout(() => this.updatePosition(), 0);
                },
            });
    }
}
